import IconDoubleChevronLeft from '@apollo/icons/default/IconDoubleChevronLeft.svg';
import IconDoubleChevronRight from '@apollo/icons/default/IconDoubleChevronRight.svg';
import IconFloppyDisk from '@apollo/icons/default/IconFloppyDisk.svg';
import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import IconServices from '@apollo/icons/default/IconServices.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Button,
  IconButton,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  customColors,
} from '@apollo/orbit';
import classnames from 'classnames';
import _, { sortBy } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { Fragment, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Link as ReactRouterLink } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { test } from 'src/components/common/typeahead/fuzzy';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { Config } from 'src/lib/config/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { StyledMenuNavLink } from '../../documentationPage/schemaPage/styledMenuNavLink/StyledMenuNavLink';
import {
  API_SCHEMA_ID,
  SUPERGRAPH_SCHEMA_ID,
} from '../../documentationPage/sdlPage/sdlDisplay/SDLDisplay';
import { GraphRef } from '../../hooks/useGraphRef';
import { proposalEditorRouteConfig } from '../../routes';

import {
  ChangedSubgraph,
  ProposalSchemaInfo,
} from './hooks/useProposalEditorState/useProposalEditorState';
import { SubgraphListSection } from './SubgraphListSection';

const SubgraphNavItem = ({
  selectedSchema,
  schemaInfo,
  totalSubgraphCount,
  subgraphHasBeenSaved,
  onSwitchEditor,
  isStale,
  canEditProposal,
  subgraphDropdownButtonRefs,
  numberOfLanguageServerErrors,
  hasChanged,
  hasGraphqlJsErrors,
}: {
  selectedSchema: string | undefined;
  schemaInfo: ProposalSchemaInfo;
  totalSubgraphCount: number;
  subgraphHasBeenSaved: (subgraphName: string) => boolean | undefined;
  onSwitchEditor: () => void;
  isStale: boolean;
  canEditProposal: boolean;
  subgraphDropdownButtonRefs: React.MutableRefObject<{
    [name: string]: React.RefObject<HTMLAnchorElement>;
  }>;
  numberOfLanguageServerErrors: number;
  hasChanged: boolean;
  hasGraphqlJsErrors: boolean;
}) => {
  const location = useLocation();
  return (
    <StyledMenuNavLink
      key={schemaInfo.id}
      ref={(el: HTMLAnchorElement | null) => {
        // eslint-disable-next-line no-param-reassign
        subgraphDropdownButtonRefs.current[schemaInfo.id] = {
          current: el,
        };
      }}
      to={proposalEditorRouteConfig.locationFrom({
        location,
        patch: {
          selectedSchema: schemaInfo.id,
        },
      })}
      onClick={onSwitchEditor}
      isActive={() => selectedSchema === schemaInfo.id}
      className="group my-1 items-center !py-1"
      activeStyle={{
        background: customColors.btn['secondary-selected'],
      }}
    >
      <IconSingleService
        className={classnames(
          'ml-0.5 mr-2',
          numberOfLanguageServerErrors > 0
            ? 'text-error'
            : selectedSchema === schemaInfo.id
              ? 'icon-info'
              : 'text-secondary',
        )}
      />
      <span
        className={classnames('flex-1 truncate font-mono text-sm', {
          'text-error': numberOfLanguageServerErrors > 0,
        })}
        title={schemaInfo.id}
      >
        {schemaInfo.id}
      </span>
      {canEditProposal ? (
        <Menu placement="bottom-start" autoSelect={false}>
          <MenuButton
            as={IconButton}
            variant="hidden"
            size="sm"
            className={classnames(
              selectedSchema === schemaInfo.id && 'group-hover:visible',
              'invisible ml-2',
            )}
          >
            <IconOverflow />
          </MenuButton>
          <MenuContent>
            <MenuList>
              {!subgraphHasBeenSaved(schemaInfo.displayName) && (
                <MenuItem
                  as={ReactRouterLink}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.renameProposalSubgraph,
                  })}
                  onClick={onSwitchEditor}
                >
                  Rename subgraph
                </MenuItem>
              )}
              <Tooltip
                disabled={!isStale && totalSubgraphCount !== 1}
                shouldWrapChildren={!(!isStale && totalSubgraphCount !== 1)}
                label={
                  isStale
                    ? 'Cannot delete, local state is out of date'
                    : 'You cannot delete the last subgraph in this proposal'
                }
                placement="right"
              >
                <MenuItem
                  as={ReactRouterLink}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.deleteProposalSubgraph,
                  })}
                  isDisabled={isStale}
                  className="text-error hover:bg-error hover:text-error"
                >
                  Delete subgraph
                </MenuItem>
              </Tooltip>
            </MenuList>
          </MenuContent>
        </Menu>
      ) : (
        <span className="ml-auto py-1 text-sm font-normal text-secondary">
          View only
        </span>
      )}
      <div className="ml-auto inline-flex items-center justify-center pl-4">
        {numberOfLanguageServerErrors > 0 ? (
          <span className="mr-2 font-mono text-xs text-error">
            {format.number(numberOfLanguageServerErrors, '0[.]0a')}
          </span>
        ) : null}
        <span
          aria-label={hasChanged ? 'Subgraph edited' : undefined}
          className={classnames(
            'inline-block size-1.5 self-center rounded-full',
            hasChanged &&
              (numberOfLanguageServerErrors > 0 || hasGraphqlJsErrors
                ? 'bg-red'
                : 'bg-grey-dark'),
          )}
        />
      </div>
    </StyledMenuNavLink>
  );
};

export const ProposalEditorNav = ({
  collapsed,
  setCollapsed,
  graphRef,
  subgraphsWithMetadata,
  graph,
  onSwitchEditor,
  buildErrors,
  graphInfoMap,
  canEditProposal,
  selectedSchema,
  changedSubgraphs,
  changedSubgraphsWithSyntaxErrors,
  subgraphDropdownButtonRefs,
  isStale,
  subgraphsHaveChanges,
  saveRevisionDisabled,
  subgraphHasBeenSaved,
  languageServerDiagnosticsBySubgraphName,
  isPullUpstream,
}: {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  graphRef: GraphRef;
  subgraphsWithMetadata: [string, ProposalSchemaInfo][];
  graph: GraphQLTypes.ProposalEditorQuery['service'];
  onSwitchEditor: () => void;
  buildErrors: GraphQLTypes.BuildError[] | undefined;
  graphInfoMap: Record<string, ProposalSchemaInfo>;
  canEditProposal: boolean;
  selectedSchema: string;
  changedSubgraphs: Record<string, ChangedSubgraph>;
  changedSubgraphsWithSyntaxErrors: [
    string,
    monacoEditor.editor.IMarkerData[],
  ][];
  subgraphDropdownButtonRefs: React.MutableRefObject<{
    [name: string]: React.RefObject<HTMLAnchorElement>;
  }>;
  isStale: boolean;
  subgraphsHaveChanges: boolean;
  saveRevisionDisabled: boolean;
  subgraphHasBeenSaved: (subgraphName: string) => boolean | undefined;
  languageServerDiagnosticsBySubgraphName: Record<
    string,
    { numberOfErrors: number }
  >;
  isPullUpstream: boolean;
}) => {
  const { pullUpstreamChanges } = useRouteParams(proposalEditorRouteConfig);

  const isPullUpstreamFlagOn = useLDFlag('nebula_proposals_merge_upstream');
  const shouldPullUpstreamChanges =
    pullUpstreamChanges === 'true' && isPullUpstreamFlagOn;

  const location = useLocation();
  const hasApiSchema = !!graphInfoMap[API_SCHEMA_ID]?.text;
  const hasSupergraphSchema = !!graphInfoMap[SUPERGRAPH_SCHEMA_ID]?.text;

  // We should use the merge base launch if it exists, but if it doesn't we should use the first launch
  const firstLaunch =
    graph?.variant?.proposal?.latestRevision?.mergeBaseLaunch ??
    graph?.variant?.firstLaunch?.[0];

  const variant = graph?.variant;

  const subgraphsChangedSinceMergeBaseOrFirstLaunch = useMemo(() => {
    return subgraphsWithMetadata
      ?.filter(
        ([subgraphName, subgraphInfo]) =>
          firstLaunch?.buildInput.__typename === 'CompositionBuildInput' &&
          !firstLaunch?.buildInput.subgraphs.some(
            (sourceSubgraph) =>
              sourceSubgraph.name === subgraphName &&
              sourceSubgraph.hash === subgraphInfo.hash,
          ),
      )
      .map(([subgraphName]) => subgraphName);
  }, [firstLaunch?.buildInput, subgraphsWithMetadata]);

  const hasEmptySchema = useMemo(() => {
    return Object.values(changedSubgraphs).some((subgraph) => subgraph.isEmpty);
  }, [changedSubgraphs]);

  const [
    uneditedSubgraphsWithMetadata,
    editedSubgraphsWithoutConflictsWithMetadata,
    editedSubgraphsWithConflictsWithMetadata,
  ] = useMemo(() => {
    const unedited: [string, ProposalSchemaInfo][] = [];
    const noConflicts: [string, ProposalSchemaInfo][] = [];
    const conflicts: [string, ProposalSchemaInfo][] = [];
    subgraphsWithMetadata.forEach(([subgraphName, schemaInfo]) => {
      const {
        changed: isChanged,
        hasConflicts,
        isUneditedFromLatestSourceVariantSdl,
      } = changedSubgraphs[subgraphName] ?? {};

      // If the subgraph has changes from the merge base or latest launch, we want to show it as changed
      // however in the upstream changes flow, subgraphs that have been added between the merge base and latest launch
      // should not show up as "changed"
      const isChangedFromStoredSchemas =
        subgraphsChangedSinceMergeBaseOrFirstLaunch?.some(
          (updatedSubgraphName) => updatedSubgraphName === subgraphName,
        ) && !schemaInfo.isNewSubgraphFromLatestSourceVariant;

      const isChangedInEditor =
        isChanged &&
        (!shouldPullUpstreamChanges || !isUneditedFromLatestSourceVariantSdl);

      if (!isChangedFromStoredSchemas && !isChangedInEditor) {
        unedited.push([subgraphName, schemaInfo]);
      } else if (!hasConflicts) {
        noConflicts.push([subgraphName, schemaInfo]);
      } else {
        conflicts.push([subgraphName, schemaInfo]);
      }
    });
    return [unedited, noConflicts, conflicts];
  }, [
    changedSubgraphs,
    shouldPullUpstreamChanges,
    subgraphsChangedSinceMergeBaseOrFirstLaunch,
    subgraphsWithMetadata,
  ]);

  const [filter, setFilter] = useState('');
  const visibleSubgraphsWithMetadata = useMemo(
    () =>
      subgraphsWithMetadata.filter(([subgraphName]) =>
        test(filter, subgraphName),
      ),
    [filter, subgraphsWithMetadata],
  );

  const [
    visibleEditedSubgraphsWithoutConflictsWithMetadata,
    visibleEditedSubgraphsWithConflictsWithMetadata,
    visibleUneditedSubgraphsWithMetadata,
  ] = useMemo(
    () => [
      editedSubgraphsWithoutConflictsWithMetadata.filter(([subgraphName]) =>
        visibleSubgraphsWithMetadata.some(
          ([visibleSubgraphName]) => visibleSubgraphName === subgraphName,
        ),
      ),
      editedSubgraphsWithConflictsWithMetadata.filter(([subgraphName]) =>
        visibleSubgraphsWithMetadata.some(
          ([visibleSubgraphName]) => visibleSubgraphName === subgraphName,
        ),
      ),
      sortBy(
        uneditedSubgraphsWithMetadata.filter(([subgraphName]) =>
          visibleSubgraphsWithMetadata.some(
            ([visibleSubgraphName]) => visibleSubgraphName === subgraphName,
          ),
        ),
        [
          ([subgraphName]) =>
            // Unedited subgraphs can be "changed" when we are in the pull changes mode
            changedSubgraphs[subgraphName]?.changed ? -1 : 1,
          ([subgraphName]) => subgraphName,
        ],
      ),
    ],
    [
      changedSubgraphs,
      editedSubgraphsWithConflictsWithMetadata,
      editedSubgraphsWithoutConflictsWithMetadata,
      uneditedSubgraphsWithMetadata,
      visibleSubgraphsWithMetadata,
    ],
  );

  return (
    <div className="max-md:min-w-[69px]">
      <div
        className={classnames(
          'relative z-40 flex h-full flex-col py-4',
          !collapsed
            ? 'w-80 px-2 max-md:absolute max-md:border-r max-md:border-primary max-md:bg-primary'
            : 'justify-between px-2',
        )}
      >
        <div
          className={classnames(
            'mb-4 flex ',
            !collapsed ? 'mx-2 justify-between' : 'justify-center',
          )}
        >
          <div className={classnames(collapsed && 'hidden')}>
            <h3 className="text-lg font-semibold">
              {graph?.name ?? graphRef.graphId}
            </h3>
            {isMinLengthArray(1, subgraphsWithMetadata) && (
              <p className="text-sm text-neutral">
                Select a schema to edit or reference.
              </p>
            )}
          </div>
          <Tooltip label={collapsed ? 'Expand' : 'Collapse'} placement="end">
            <IconButton
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              icon={
                collapsed ? (
                  <IconDoubleChevronRight />
                ) : (
                  <IconDoubleChevronLeft />
                )
              }
              variant="hidden"
              size="sm"
              onClick={() => setCollapsed((isCollapsed) => !isCollapsed)}
            />
          </Tooltip>
        </div>
        <div
          className={classnames(
            'flex min-h-0 grow flex-col space-y-6 overflow-y-auto px-2 py-1',
            collapsed && 'hidden',
          )}
        >
          <div className="flex flex-col space-y-2">
            <div className="mb-2 flex items-center justify-between border-b border-primary pb-2">
              <div className="font-semibold text-neutral">Composed schemas</div>
              <div className="ml-2 text-sm text-secondary">View only</div>
            </div>
            {hasApiSchema || hasSupergraphSchema ? (
              <>
                {hasApiSchema && (
                  <StyledMenuNavLink
                    to={proposalEditorRouteConfig.locationFrom({
                      location,
                      patch: {
                        selectedSchema: API_SCHEMA_ID,
                        linkedCoordinateName: RouteConfig.DELETE,
                        linkedCommentId: RouteConfig.DELETE,
                      },
                    })}
                    onClick={onSwitchEditor}
                    // we should never style this tab as active if it is the only item in the sidebar
                    isActive={() =>
                      isMinLengthArray(2, Object.keys(graphInfoMap)) &&
                      selectedSchema === API_SCHEMA_ID
                    }
                    className={classnames(
                      'my-1 flex items-center',
                      !isMinLengthArray(2, Object.keys(graphInfoMap)) &&
                        'hover:cursor-auto hover:bg-transparent',
                    )}
                  >
                    <IconServices
                      className={classnames(
                        'mr-1.5',
                        selectedSchema === API_SCHEMA_ID
                          ? 'icon-info'
                          : 'text-secondary',
                      )}
                    />
                    <span className="font-mono text-sm">API</span>

                    {isMinLengthArray(1, buildErrors) && (
                      <IconWarning className="ml-2 text-icon-warning" />
                    )}
                  </StyledMenuNavLink>
                )}
                {hasSupergraphSchema && (
                  <StyledMenuNavLink
                    to={proposalEditorRouteConfig.locationFrom({
                      location,
                      patch: {
                        selectedSchema: SUPERGRAPH_SCHEMA_ID,
                        linkedCoordinateName: RouteConfig.DELETE,
                        linkedCommentId: RouteConfig.DELETE,
                      },
                    })}
                    onClick={onSwitchEditor}
                    isActive={() => selectedSchema === SUPERGRAPH_SCHEMA_ID}
                    className="my-1 flex items-center"
                  >
                    <IconServices
                      className={classnames(
                        'mr-1.5',
                        selectedSchema === SUPERGRAPH_SCHEMA_ID
                          ? 'icon-info'
                          : 'text-secondary',
                      )}
                    />
                    <span className="font-mono text-sm">Supergraph</span>

                    {isMinLengthArray(1, buildErrors) && (
                      <IconWarning className="ml-2 text-icon-warning" />
                    )}
                  </StyledMenuNavLink>
                )}
              </>
            ) : isMinLengthArray(1, buildErrors) ? (
              <p className="-mt-6 text-sm text-neutral">
                Composition has never succeeded on this proposal, so no composed
                schemas exist
              </p>
            ) : (
              <p className="-mt-6 text-sm text-neutral">
                This proposal is still building, composed schemas will be
                available asap.
              </p>
            )}
          </div>
          {isMinLengthArray(1, subgraphsWithMetadata) && (
            <div className="flex flex-col space-y-2">
              <div className="mb-2 flex items-center justify-between border-b border-primary pb-2">
                <div className="font-semibold text-neutral">
                  Subgraph schemas
                </div>
                {canEditProposal && (
                  <Link
                    onClick={onSwitchEditor}
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.addProposalSubgraph,
                    })}
                    className="text-sm"
                  >
                    + Add new
                  </Link>
                )}
              </div>
              {isMinLengthArray(10, subgraphsWithMetadata) && (
                <Input
                  placeholder="Filter subgraphs"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  leftElement={<IconSearch className="pointer-events-none" />}
                />
              )}
              {isMinLengthArray(
                1,
                editedSubgraphsWithoutConflictsWithMetadata,
              ) ||
              isMinLengthArray(1, editedSubgraphsWithConflictsWithMetadata) ? (
                <>
                  {isMinLengthArray(
                    1,
                    editedSubgraphsWithConflictsWithMetadata,
                  ) && (
                    <SubgraphListSection
                      label="Subgraphs with conflicts"
                      hasLanguageServerErrors={false}
                    >
                      {visibleEditedSubgraphsWithConflictsWithMetadata.map(
                        ([, subgraph]) => (
                          <SubgraphNavItem
                            key={subgraph.id}
                            totalSubgraphCount={subgraphsWithMetadata.length}
                            canEditProposal={canEditProposal}
                            selectedSchema={selectedSchema}
                            isStale={isStale}
                            onSwitchEditor={onSwitchEditor}
                            schemaInfo={subgraph}
                            subgraphDropdownButtonRefs={
                              subgraphDropdownButtonRefs
                            }
                            subgraphHasBeenSaved={subgraphHasBeenSaved}
                            numberOfLanguageServerErrors={
                              languageServerDiagnosticsBySubgraphName[
                                subgraph.id
                              ]?.numberOfErrors ?? 0
                            }
                            hasChanged={
                              !!changedSubgraphs[subgraph.id]?.changed
                            }
                            hasGraphqlJsErrors={
                              changedSubgraphs[subgraph.id]
                                ?.hasGraphqlJsSyntaxErrors ?? false
                            }
                          />
                        ),
                      )}
                    </SubgraphListSection>
                  )}
                  {isMinLengthArray(
                    1,
                    editedSubgraphsWithoutConflictsWithMetadata,
                  ) && (
                    <SubgraphListSection
                      label="Edited in this proposal"
                      hasLanguageServerErrors={false}
                    >
                      {visibleEditedSubgraphsWithoutConflictsWithMetadata.map(
                        ([, subgraph]) => (
                          <SubgraphNavItem
                            key={subgraph.id}
                            totalSubgraphCount={subgraphsWithMetadata.length}
                            canEditProposal={canEditProposal}
                            selectedSchema={selectedSchema}
                            isStale={isStale}
                            onSwitchEditor={onSwitchEditor}
                            schemaInfo={subgraph}
                            subgraphDropdownButtonRefs={
                              subgraphDropdownButtonRefs
                            }
                            subgraphHasBeenSaved={subgraphHasBeenSaved}
                            numberOfLanguageServerErrors={
                              languageServerDiagnosticsBySubgraphName[
                                subgraph.id
                              ]?.numberOfErrors ?? 0
                            }
                            hasChanged={
                              !!changedSubgraphs[subgraph.id]?.changed
                            }
                            hasGraphqlJsErrors={
                              changedSubgraphs[subgraph.id]
                                ?.hasGraphqlJsSyntaxErrors ?? false
                            }
                          />
                        ),
                      )}
                    </SubgraphListSection>
                  )}
                  {isMinLengthArray(1, uneditedSubgraphsWithMetadata) && (
                    <SubgraphListSection
                      label="Other"
                      showSectionDefault={
                        !isMinLengthArray(
                          1,
                          variant?.updatedSubgraphsSinceCreation,
                        ) ||
                        uneditedSubgraphsWithMetadata.some(
                          ([subgraphName]) =>
                            subgraphName === selectedSchema ||
                            // If we are in the pull changes mode, "unedited subgraphs"
                            // may be "changed" and if so we should open this
                            changedSubgraphs[subgraphName]?.changed,
                        )
                      }
                      hasLanguageServerErrors={visibleUneditedSubgraphsWithMetadata.some(
                        ([, subgraph]) =>
                          (languageServerDiagnosticsBySubgraphName[subgraph.id]
                            ?.numberOfErrors ?? 0) > 0,
                      )}
                    >
                      {visibleUneditedSubgraphsWithMetadata.map(
                        ([, subgraph]) => (
                          <SubgraphNavItem
                            key={subgraph.id}
                            totalSubgraphCount={subgraphsWithMetadata.length}
                            canEditProposal={canEditProposal}
                            selectedSchema={selectedSchema}
                            isStale={isStale}
                            onSwitchEditor={onSwitchEditor}
                            schemaInfo={subgraph}
                            subgraphDropdownButtonRefs={
                              subgraphDropdownButtonRefs
                            }
                            subgraphHasBeenSaved={subgraphHasBeenSaved}
                            numberOfLanguageServerErrors={
                              languageServerDiagnosticsBySubgraphName[
                                subgraph.id
                              ]?.numberOfErrors ?? 0
                            }
                            hasChanged={
                              !!changedSubgraphs[subgraph.id]?.changed
                            }
                            hasGraphqlJsErrors={false}
                          />
                        ),
                      )}
                    </SubgraphListSection>
                  )}
                </>
              ) : (
                visibleSubgraphsWithMetadata.map(([, subgraph]) => (
                  <SubgraphNavItem
                    key={subgraph.id}
                    totalSubgraphCount={subgraphsWithMetadata.length}
                    canEditProposal={canEditProposal}
                    selectedSchema={selectedSchema}
                    isStale={isStale}
                    onSwitchEditor={onSwitchEditor}
                    schemaInfo={subgraph}
                    subgraphDropdownButtonRefs={subgraphDropdownButtonRefs}
                    subgraphHasBeenSaved={subgraphHasBeenSaved}
                    numberOfLanguageServerErrors={
                      languageServerDiagnosticsBySubgraphName[subgraph.id]
                        ?.numberOfErrors ?? 0
                    }
                    hasChanged={false}
                    hasGraphqlJsErrors={false}
                  />
                ))
              )}
            </div>
          )}
        </div>
        {isMinLengthArray(1, subgraphsWithMetadata) && canEditProposal && (
          <div
            className={classnames(
              'flex px-2 pb-2 [&_>_span]:block',
              collapsed
                ? 'flex-col items-center space-y-2'
                : 'items-center justify-between space-x-2',
            )}
          >
            {isPullUpstreamFlagOn && (
              <Tooltip
                label={
                  subgraphsHaveChanges
                    ? 'Discard unsaved changes'
                    : isStale
                      ? 'Your local schema is out of date. Try refreshing the page.'
                      : 'No changes to discard'
                }
              >
                <Button
                  variant="primary"
                  className={classnames(!collapsed && 'w-full')}
                  isDisabled={
                    !canEditProposal || !subgraphsHaveChanges || isStale
                  }
                  as={ReactRouterLink}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.discardChanges,
                  })}
                  {...(collapsed
                    ? {
                        leftIcon: <IconTrash />,
                      }
                    : {})}
                >
                  {!collapsed ? 'Discard' : null}
                </Button>
              </Tooltip>
            )}

            <Tooltip
              label={
                hasEmptySchema
                  ? 'Cannot save an empty schema'
                  : !subgraphsHaveChanges && !isPullUpstream
                    ? `No changes to save`
                    : isStale
                      ? `Your local schema is out of date. Try refreshing the page.`
                      : isMinLengthArray(1, changedSubgraphsWithSyntaxErrors)
                        ? changedSubgraphsWithSyntaxErrors.map(
                            ([subgraph, errors]) => (
                              <Fragment key={subgraph}>
                                <div>{subgraph} has invalid SDL:</div>
                                {errors.map((error) => (
                                  <div
                                    key={`${error.startLineNumber}${error.startColumn}${error.message}`}
                                  >
                                    ln:{error.startLineNumber} - {error.message}
                                  </div>
                                ))}
                              </Fragment>
                            ),
                          )
                        : null
              }
              disabled={
                subgraphsHaveChanges &&
                !isStale &&
                !hasEmptySchema &&
                !isMinLengthArray(1, changedSubgraphsWithSyntaxErrors)
              }
              placement="right"
            >
              <Button
                // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                // TODO(Maya) add link to synthetics test
                data-testid="proposals_save_revision_button_editor"
                variant="primary"
                className={classnames(!collapsed && 'w-full')}
                isDisabled={
                  hasEmptySchema ||
                  !canEditProposal ||
                  (!subgraphsHaveChanges && !isPullUpstream) ||
                  isStale ||
                  isMinLengthArray(1, changedSubgraphsWithSyntaxErrors)
                }
                onClick={onSwitchEditor}
                isLoading={saveRevisionDisabled}
                loadingText="Saving..."
                as={ReactRouterLink}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.publishRevision,
                })}
                {...(collapsed
                  ? {
                      leftIcon: <IconFloppyDisk />,
                    }
                  : {})}
              >
                {!collapsed
                  ? isPullUpstream
                    ? 'Save Merge'
                    : 'Save Revision'
                  : null}
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};
